import React, { useEffect, useCallback, useMemo } from 'react';

import useCarriers from '../../../hooks/useCarriers';
import useSubmitClaim from './useSubmitClaim';
import CreateClaim from '../../parcel-finder/claims/CreateClaim';
import useSearchShipment from './useSearchShipment';
import { trackEvent } from '../../../utils/googleAnalytics';
import machine from './stateMachine';

function CreateClaimContainer({ onClose, onClaimSubmitted }) {
  const { claim, isSubmitting, isClaimsAutomated, changeClaim, resetClaim, submitClaim } = useSubmitClaim();
  const { isSearching, nonClaimableMessage, resetShipmentSearch, loadShipmentByTrackingId } = useSearchShipment({
    claim,
    changeClaim,
    resetClaim,
  });
  const { carriers } = useCarriers();
  const carrierOptions = useMemo(
    () => getUniqueCarriers(carriers).map(({ name, code }) => ({ label: name || code, value: code })),
    [carriers]
  );
  const countryOptions = useMemo(() => getCarrierCountryOptions(claim.carrierCode, carriers), [carriers, claim]);

  useEffect(() => {
    trackClaimAction('Claim initiated');
  }, []);

  const onCloseHandler = useCallback(
    (step) => {
      trackClaimAction(`Claim exit: ${step}`);
      onClose();
    },
    [onClose]
  );

  const onSubmitHandler = useCallback(async () => {
    const { success } = await submitClaim();

    if (success) {
      trackClaimAction('Claim submitted');
      onClaimSubmitted();
      onClose();
    }
  }, [submitClaim, onClose, onClaimSubmitted]);

  return (
    <CreateClaim
      claim={claim}
      resetClaim={resetClaim}
      carrierOptions={carrierOptions}
      carrierCountryOptions={countryOptions}
      machine={machine}
      onChange={changeClaim}
      onLoadShipmentByTrackingId={loadShipmentByTrackingId}
      onResetShipmentSearch={resetShipmentSearch}
      onClose={onCloseHandler}
      onSubmit={onSubmitHandler}
      isSubmitting={isSubmitting || isSearching}
      isDisabled={isSearching}
      nonClaimableMessage={nonClaimableMessage}
      isClaimsAutomated={isClaimsAutomated}
    />
  );
}

function getUniqueCarriers(carriers) {
  return [...new Map(carriers.map((carrier) => [carrier.code, carrier])).values()];
}

function getCarrierCountryOptions(carrierCode, carriers) {
  return carriers
    .filter(({ code }) => code === carrierCode)
    .map(({ country }) => ({ label: country.name, value: country.iso2 }));
}

function trackClaimAction(action) {
  return trackEvent({
    category: 'claims-center',
    action,
  });
}

export default React.memo(CreateClaimContainer);
